import {
  Button,
  SyntheticButton,
  YesObject
} from '@yes.technology/react-toolkit'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'

import { DataCardsButtonProps } from 'site/Renderer/components/DataCards/DataCards.types'
import { StatusAction } from 'status/shared/situation.type'

import buildSyntheticActionOptions from './helpers/buildSyntheticActionOptions'
import getOutputTaskVisualizeHref from './helpers/getOutputTaskVisualizeHref'
import { TaskActionsContainer, TaskActionsRow } from './TaskObjectCard.styles'

export type SyntheticTaskActionsProps = {
  statusUuid: string
  displayedStatusActions: StatusAction[]
  showOnlyVisualizeButton: boolean
  changeStatus: (statusUuid: string) => void
  executeHref: string
  buttonProps?: DataCardsButtonProps
  taskmodelUuid?: string
  workflow?: YesObject[]
  visualizeModal: ReactNode
  onOpenVisualize: () => void
}

export default function SyntheticTaskActions({
  statusUuid,
  displayedStatusActions,
  showOnlyVisualizeButton,
  changeStatus,
  executeHref,
  buttonProps,
  taskmodelUuid,
  workflow,
  visualizeModal,
  onOpenVisualize
}: SyntheticTaskActionsProps) {
  const { t } = useTranslation()

  const isWaitingInteraction =
    window.waitingInteractionStatusUuid === statusUuid

  const outputTaskVisualizeHref = getOutputTaskVisualizeHref(
    taskmodelUuid,
    workflow
  )

  const fullWidthVisualizeButton = (
    <Button
      key='view'
      variant='secondary'
      heightClass='small'
      style={{ width: '100%' }}
      data-testid='synthetic-visualize-button'
      onClick={onOpenVisualize}
    >
      {t('task.action.view')}
    </Button>
  )

  const syntheticActionOptions = useMemo(
    () =>
      buildSyntheticActionOptions({
        isWaitingInteraction,
        buttonProps,
        executeLabel: t('task.action.execute'),
        executeHref,
        viewLabel: t('task.action.view'),
        displayedStatusActions,
        changeStatus,
        outputTaskVisualizeHref
      }),
    [
      isWaitingInteraction,
      buttonProps,
      t,
      executeHref,
      displayedStatusActions,
      changeStatus,
      outputTaskVisualizeHref
    ]
  )

  if (
    showOnlyVisualizeButton &&
    !isWaitingInteraction &&
    !outputTaskVisualizeHref
  ) {
    return (
      <TaskActionsContainer>
        <TaskActionsRow $fullWidth $variant='synthetic'>
          {fullWidthVisualizeButton}
        </TaskActionsRow>
        {visualizeModal}
      </TaskActionsContainer>
    )
  }

  const syntheticActionsDropdownLabel =
    syntheticActionOptions[0]?.children ?? t('task.action.execute')

  const shouldShowVisualizeButton = !outputTaskVisualizeHref

  return (
    <TaskActionsContainer>
      {syntheticActionOptions.length > 0 && (
        <TaskActionsRow $fullWidth $variant='synthetic'>
          <SyntheticButton
            options={syntheticActionOptions}
            dropdownLabel={syntheticActionsDropdownLabel}
            variant='primary'
            heightClass='small'
          />
        </TaskActionsRow>
      )}
      {shouldShowVisualizeButton && (
        <TaskActionsRow $fullWidth $variant='synthetic'>
          {fullWidthVisualizeButton}
        </TaskActionsRow>
      )}
      {visualizeModal}
    </TaskActionsContainer>
  )
}
